import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';
import type {
  AppointmentsDetailsResponse,
  AppointmentStatus,
  UpdateAppointmentStatusCommand,
} from '@/types/scheduling-api';

/**
 * PUT /api/Appointment/Status — change an appointment's status code.
 *
 * Mirrors Flutter's `updateStatus` (scheduling_data_source_impl.dart:373).
 * Body shape matches `UpdateStatusRequest`:
 *   { appointmentId, updatedBy, status: { code, display, definition } }
 *
 * Optimistic update: writes the new status into every cached
 * AppointmentsDetailsResponse via setQueryData so the row reflects
 * immediately. The SignalR `UpdateAppointments` push from the server
 * follows shortly and reconciles any drift via the same merge path.
 *
 * TWO CACHES HOLD AN APPOINTMENT'S STATUS, and the optimistic patch above only reaches one.
 * `['appointments', …]` is the calendar's LIST; `['appointment-details', id]` is the SINGLE
 * appointment read (`encounter/services/use-appointment-status.ts:110`). The `onSuccess` below
 * refreshes the second, so every caller of this mutation gets it for free.
 *
 * WHO ACTUALLY NEEDED THIS — stated precisely, because it is easy to over-credit. Three of the four
 * call sites already invalidated `['appointment-details']` themselves:
 *   · `encounter/encounter-status-control.tsx:91`               — already did
 *   · `scheduling/…/appointment-status-manager.tsx:209,275,290` — already did
 * The two that did NOT are `components/blocks/day-list-calendar.tsx` and
 * `patients/components/appointments/patient-appointment-row-actions.tsx`. Those are the surfaces
 * this fixes; putting it in the mutation rather than in each caller means the next call site
 * cannot forget it.
 *
 * NOT the cause of Andrew's 2026-08-27 report ("in dev inside the pre-encounter screen, when I
 * changed the appointment status from booked to arrived, nothing happened"). That symptom was the
 * status PRECEDENCE bug, fixed in 375c5bc (PROD-2030): the encounter read
 * `nav?.appointmentStatus ?? query`, so the stale tap-time snapshot beat every refetch and no
 * amount of invalidation could have shown the new status. The control's own invalidation here was
 * already firing. Recording that so the next reader doesn't credit this line for that fix.
 *
 * Also distinct from the missing realtime subscription (PROD-2028,
 * `encounter/realtime/use-encounter-appointment-realtime.ts`), which is about changes arriving from
 * ELSEWHERE — another user, or the backend's own transition on CreateEncounter/sign.
 */
export function useUpdateAppointmentStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { appointmentId: string; status: AppointmentStatus }) => {
      // Empty-string Guid here triggers a .NET model-binder 400 (see
      // CLAUDE.md "API wire-shape contract"). The user should always be
      // authenticated when this mutation fires; throwing surfaces the
      // bug via the global error toast instead of a silent 400.
      const userId = useAuthStore.getState().user?.userId;
      if (!userId) {
        throw new Error('Cannot update appointment status: no authenticated user');
      }
      const command: UpdateAppointmentStatusCommand = {
        appointmentId: args.appointmentId,
        updatedBy: userId,
        status: args.status,
      };
      return put<string>('/api/Appointment/Status', command);
    },
    onMutate: ({ appointmentId, status }) => {
      // Snapshot every appointments-cache key so we can roll back on error.
      const snapshots: { key: readonly unknown[]; data: unknown }[] = [];
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['appointments'] })
        .forEach((q) => {
          snapshots.push({ key: q.queryKey, data: q.state.data });
        });

      // Apply the optimistic status to every cached response that contains
      // this appointment id.
      queryClient.setQueriesData<AppointmentsDetailsResponse | undefined>(
        { queryKey: ['appointments'] },
        (prev) => {
          if (!prev?.appointments) return prev;
          const idx = prev.appointments.findIndex((a) => a.id === appointmentId);
          if (idx < 0) return prev;
          const next = prev.appointments.slice();
          next[idx] = { ...next[idx], status };
          return recountSummary({ ...prev, appointments: next });
        },
      );

      return { snapshots };
    },
    onSuccess: (_data, { appointmentId }) => {
      // The single-appointment read. Callers that already invalidate this prefix themselves are
      // unaffected (the same query is simply marked stale twice, which costs no extra fetch); the
      // callers that don't — the day-list calendar and the patient appointment rows — stop showing
      // a status the server no longer holds.
      void queryClient.invalidateQueries({
        queryKey: ['appointment-details', appointmentId],
      });
    },
    onError: (_err, _vars, ctx) => {
      // Roll back every cache key we touched.
      ctx?.snapshots.forEach(({ key, data }) =>
        queryClient.setQueryData(key, data),
      );
    },
  });
}

/**
 * Recompute the summary counts (waitingRoom / noShow / cancelled) so the
 * filter pills don't drift after an optimistic status change.
 */
function recountSummary(
  response: AppointmentsDetailsResponse,
): AppointmentsDetailsResponse {
  let waitingRoom = 0;
  let noShow = 0;
  let cancelled = 0;
  for (const a of response.appointments ?? []) {
    const code = a.status?.code?.toLowerCase();
    if (code === 'checkedin') waitingRoom++;
    else if (code === 'noshow') noShow++;
    else if (code === 'cancelled') cancelled++;
  }
  return { ...response, waitingRoom, noShow, cancelled };
}
